import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { WsMessage, Order } from '../types';
import { userPool } from '../lib/cognito';

// Resolve the current user's Cognito id token for the WebSocket handshake.
// Browsers can't set headers on a WS upgrade, so the $connect authorizer reads
// it from the query string instead.
function getIdToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: { isValid: () => boolean; getIdToken: () => { getJwtToken: () => string } } | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

// ── Context Types ────────────────────────────────────────────────────────────
type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WebSocketContextValue {
  status: WsStatus;
  lastMessage: WsMessage | null;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  status: 'disconnected',
  lastMessage: null,
});

export const useWebSocket = () => useContext(WebSocketContext);

// ── Provider ─────────────────────────────────────────────────────────────────
interface WebSocketProviderProps {
  restaurantId: string;
  onOrderCreated?: (order: Order) => void;
  onOrderUpdated?: (order: Order) => void;
  children: ReactNode;
}

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:3001';
const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  restaurantId,
  onOrderCreated,
  onOrderUpdated,
  children,
}) => {
  const [status, setStatus] = useState<WsStatus>('disconnected');
  const [lastMessage, setLastMessage] = useState<WsMessage | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  // ── Keep callback refs up-to-date WITHOUT causing WS reconnections ─────────
  // Using refs here means onmessage always calls the latest version of the
  // callback, but the WebSocket itself is NOT recreated when they change.
  const onOrderCreatedRef = useRef(onOrderCreated);
  const onOrderUpdatedRef = useRef(onOrderUpdated);
  useEffect(() => { onOrderCreatedRef.current = onOrderCreated; }, [onOrderCreated]);
  useEffect(() => { onOrderUpdatedRef.current = onOrderUpdated; }, [onOrderUpdated]);

  const connect = useCallback(async () => {
    if (!isMounted.current) return;

    setStatus('connecting');

    const token = await getIdToken();
    if (!isMounted.current) return;
    if (!token) {
      console.error('[WS] No valid session — cannot open authenticated socket');
      setStatus('error');
      return;
    }

    const endpoint = `${WS_URL}?restaurantId=${encodeURIComponent(restaurantId)}&token=${encodeURIComponent(token)}`;
    console.log('[WS] Connecting…');

    const ws = new WebSocket(endpoint);

    ws.onopen = () => {
      if (!isMounted.current) return;
      console.log('[WS] Connected');
      setStatus('connected');
      reconnectAttempts.current = 0;
    };

    ws.onmessage = (event: MessageEvent) => {
      if (!isMounted.current) return;
      try {
        const msg: WsMessage = JSON.parse(event.data as string);
        setLastMessage(msg);
        console.log('[WS] Message received:', msg.type);

        switch (msg.type) {
          case 'ORDER_CREATED':
            // Use ref so this always calls the latest callback without
            // requiring the WebSocket to be recreated on prop changes
            onOrderCreatedRef.current?.(msg.data as Order);
            break;
          case 'ORDER_UPDATED':
            onOrderUpdatedRef.current?.(msg.data as Order);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('[WS] Failed to parse message:', err);
      }
    };

    ws.onerror = (err) => {
      console.error('[WS] Error:', err);
      setStatus('error');
    };

    ws.onclose = () => {
      if (!isMounted.current) return;
      console.log('[WS] Disconnected');
      setStatus('disconnected');
      wsRef.current = null;

      if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = RECONNECT_DELAY_MS * Math.pow(1.5, reconnectAttempts.current);
        reconnectAttempts.current += 1;
        console.log(`[WS] Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts.current})`);
        reconnectTimer.current = setTimeout(connect, delay);
      } else {
        console.error('[WS] Max reconnect attempts reached');
        setStatus('error');
      }
    };

    wsRef.current = ws;
    // ⚠️ Only restaurantId as dep — NOT the callbacks.
    // Callbacks are read via refs in onmessage, keeping the connection stable.
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isMounted.current = true;
    connect();

    return () => {
      isMounted.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return (
    <WebSocketContext.Provider value={{ status, lastMessage }}>
      {children}
    </WebSocketContext.Provider>
  );
};
